import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GeoJson } from "pigeon-maps";
import {
  featureTouchesBounds,
  type MapBounds,
} from "../../utils/map-viewport";
import {
  GEOJSON_URL,
  loadSanitizedGeoJson,
} from "../../utils/geojson-cache";

type LatLng = [number, number];

type CountryShapesOverlayProps = {
  zoom: number;
  hoveredCountrySlug: string | null;
  activeCountrySlug: string | null;
  regionsVisible?: boolean;
  activeRegionSlug?: string | null;
  hoveredRegionSlug?: string | null;
  darkBasemap?: boolean;
  /** Settled viewport — used to cull polygons while panning stays smooth. */
  viewBounds?: MapBounds | null;
  /** While the user is dragging, keep last settled GeoJSON snapshot. */
  isMoving?: boolean;
  /**
   * Country/region page: only draw the active (+ hover) outline.
   * Never SVG-veil the whole world (that froze /italy/).
   */
  activeOnly?: boolean;
  onCountryClick: (slug: string, lat: number, lng: number) => void;
  onCountryEnter: (country: {
    slug: string;
    name: string;
    lat: number;
    lng: number;
  }) => void;
  onCountryLeave: () => void;
  mapState?: {
    width: number;
    height: number;
    center: LatLng;
    zoom: number;
    bounds?: unknown;
  };
  latLngToPixel?: (latLng: LatLng, center?: LatLng, zoom?: number) => [number, number];
  pixelToLatLng?: (pixel: [number, number]) => LatLng;
};

const DETAIL_ZOOM = 6;

/**
 * Country outlines with coarse/detail LOD + viewport cull for pan performance.
 */
const CountryShapesOverlay: React.FC<CountryShapesOverlayProps> = ({
  zoom,
  hoveredCountrySlug,
  activeCountrySlug,
  regionsVisible = false,
  activeRegionSlug = null,
  hoveredRegionSlug = null,
  darkBasemap = false,
  viewBounds = null,
  isMoving = false,
  activeOnly = false,
  onCountryClick,
  onCountryEnter,
  onCountryLeave,
  mapState,
  latLngToPixel,
  pixelToLatLng,
}) => {
  const [coarse, setCoarse] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [deferReady, setDeferReady] = useState(!activeOnly);
  const frozenRef = useRef<any>(null);

  // Country pages: don't touch GeoJSON until the content column has painted
  useEffect(() => {
    if (!activeOnly) {
      setDeferReady(true);
      return;
    }
    setDeferReady(false);
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const ready = () => setDeferReady(true);
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(ready, { timeout: 1200 });
    } else {
      timeoutId = setTimeout(ready, 100);
    }
    return () => {
      if (idleId != null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [activeOnly, activeCountrySlug]);

  useEffect(() => {
    if (!deferReady) return;
    let cancelled = false;
    loadSanitizedGeoJson(GEOJSON_URL.countries).then((json) => {
      if (!cancelled) setCoarse(json);
    });
    return () => {
      cancelled = true;
    };
  }, [deferReady]);

  useEffect(() => {
    if (!deferReady) return;
    if (activeOnly) return; // never need detail polygons for a single ring
    if (zoom < DETAIL_ZOOM) return;
    if (detail) return;
    let cancelled = false;
    loadSanitizedGeoJson(GEOJSON_URL.countriesDetail).then((json) => {
      if (!cancelled && json) setDetail(json);
    });
    return () => {
      cancelled = true;
    };
  }, [zoom, detail, deferReady, activeOnly]);

  const regionTakesFocus = Boolean(activeRegionSlug || hoveredRegionSlug);

  const visibleData = useMemo(() => {
    if (!deferReady) return null;
    const source =
      !activeOnly && zoom >= DETAIL_ZOOM && detail?.features?.length
        ? detail
        : coarse;
    if (!source?.features) return null;
    const keep = new Set(
      [activeCountrySlug, hoveredCountrySlug].filter(Boolean) as string[]
    );

    // Country focus: only the active (+ hover) outline
    if (activeOnly || (activeCountrySlug && zoom >= 4)) {
      const features = source.features.filter((f: any) =>
        keep.has(f?.properties?.slug)
      );
      return { type: "FeatureCollection", features };
    }

    // World browse at low zoom: need all outlines for hit-testing
    if (zoom < 4 || !viewBounds) return source;

    const features = source.features.filter((f: any) =>
      featureTouchesBounds(f, viewBounds, keep)
    );
    return { type: "FeatureCollection", features };
  }, [
    coarse,
    detail,
    zoom,
    viewBounds,
    activeCountrySlug,
    hoveredCountrySlug,
    activeOnly,
    deferReady,
  ]);

  const data = useMemo(() => {
    if (isMoving && frozenRef.current) return frozenRef.current;
    if (visibleData) frozenRef.current = visibleData;
    return visibleData;
  }, [visibleData, isMoving]);

  const hoverData = useMemo(() => {
    if (isMoving) return null;
    if (!data || !hoveredCountrySlug || hoveredCountrySlug === activeCountrySlug) {
      return null;
    }
    const feature = data.features?.find(
      (f: any) => f?.properties?.slug === hoveredCountrySlug
    );
    if (!feature) return null;
    return { type: "FeatureCollection", features: [feature] };
  }, [data, hoveredCountrySlug, activeCountrySlug, isMoving]);

  // Strong selection ring on its own layer (was nearly invisible when regionsVisible)
  const activeData = useMemo(() => {
    if (isMoving || !data || !activeCountrySlug) return null;
    const feature = data.features?.find(
      (f: any) => f?.properties?.slug === activeCountrySlug
    );
    if (!feature) return null;
    return { type: "FeatureCollection", features: [feature] };
  }, [data, activeCountrySlug, isMoving]);

  const styleCallback = useCallback(
    (feature: any) => {
      const slug = feature?.properties?.slug as string | undefined;
      const isActive = Boolean(slug && slug === activeCountrySlug);
      const isHoveredOther =
        Boolean(slug) &&
        slug === hoveredCountrySlug &&
        slug !== activeCountrySlug;
      const focusing = Boolean(activeCountrySlug);

      if (isActive) {
        // Soft fill on the selected country (veil of all others removed — that froze Italy)
        return {
          fill: darkBasemap
            ? "rgba(255, 255, 255, 0.06)"
            : "rgba(0, 83, 25, 0.1)",
          stroke: "rgba(0, 0, 0, 0)",
          strokeWidth: "0",
          pointerEvents: "none",
          r: "0",
        };
      }

      if (focusing) {
        // Only neighbors/hover reach here when country focus culls the world
        const veil = darkBasemap
          ? "rgba(255, 255, 255, 0.22)"
          : "rgba(0, 0, 0, 0.28)";
        const stroke = darkBasemap
          ? "rgba(255, 255, 255, 0.12)"
          : "rgba(0, 0, 0, 0.14)";
        if (isHoveredOther) {
          return {
            fill: darkBasemap
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.1)",
            stroke: "rgba(255, 255, 255, 0.35)",
            strokeWidth: "0.75",
            strokeLinejoin: "round",
            pointerEvents: "none",
            r: "0",
          };
        }
        return {
          fill: veil,
          stroke,
          strokeWidth: "0.4",
          strokeLinejoin: "round",
          pointerEvents: "none",
          r: "0",
        };
      }

      return {
        fill: "rgba(0, 0, 0, 0)",
        stroke: darkBasemap
          ? "rgba(255, 255, 255, 0.28)"
          : "rgba(0, 0, 0, 0.22)",
        strokeWidth: zoom <= 3 ? "0.6" : "0.85",
        strokeLinejoin: "round",
        // World view: clicks go through stroke hit-testing via GeoJson; keep none on fill
        pointerEvents: zoom <= 5 ? "stroke" : "none",
        r: "0",
      };
    },
    [
      activeCountrySlug,
      hoveredCountrySlug,
      darkBasemap,
      zoom,
    ]
  );

  const activeStyleCallback = useCallback(() => {
    // Keep country selection obvious even with region overlays on top
    const strokeW = regionTakesFocus
      ? zoom <= 5
        ? 2.5
        : 2.25
      : zoom <= 3.5
        ? 4.5
        : zoom <= 5
          ? 4
          : zoom <= 8
            ? 3.5
            : 3;
    return {
      fill: "rgba(0, 0, 0, 0)",
      stroke: "#ffffff",
      strokeWidth: String(strokeW),
      strokeLinejoin: "round",
      strokeLinecap: "round",
      paintOrder: "stroke fill",
      pointerEvents: "none",
      r: "0",
    };
  }, [zoom, regionTakesFocus]);

  const hoverStyleCallback = useCallback(() => {
    const strokeW =
      zoom <= 3.5
        ? 2.25
        : zoom <= 5
          ? 2
          : zoom <= 8
            ? 1.75
            : zoom <= 11
              ? 1.5
              : 1.25;
    return {
      fill: "rgba(0, 0, 0, 0)",
      stroke: "#ffffff",
      strokeWidth: String(strokeW),
      strokeDasharray: activeCountrySlug ? "5 3.5" : "3.5 3",
      strokeLinejoin: "round",
      strokeLinecap: "round",
      paintOrder: "stroke fill",
      pointerEvents: "none",
      r: "0",
    };
  }, [zoom, activeCountrySlug]);

  if (!data || !mapState?.width || !mapState?.height || !latLngToPixel) {
    return null;
  }
  // Drop SVG entirely while panning — reprojection every frame freezes the UI
  if (isMoving) return null;
  // Point/close-up zoom: basemap is enough; country polygons only hurt
  if (zoom >= 8) return null;

  const mapProps = {
    mapState,
    latLngToPixel,
    pixelToLatLng,
  };

  return (
    <>
      <GeoJson
        {...mapProps}
        data={data}
        hover={false}
        styleCallback={styleCallback}
        onClick={({ payload }) => {
          const props = payload?.properties;
          if (!props?.slug) return;
          onCountryClick(
            props.slug,
            Number(props.lat) || 0,
            Number(props.lng) || 0
          );
        }}
        onMouseOver={({ payload }) => {
          const props = payload?.properties;
          if (!props?.slug) return;
          onCountryEnter({
            slug: props.slug,
            name: props.name,
            lat: Number(props.lat) || 0,
            lng: Number(props.lng) || 0,
          });
        }}
        onMouseOut={({ event }) => {
          const e = event as unknown as React.MouseEvent<SVGGElement>;
          const related = e.relatedTarget as Node | null;
          const current = e.currentTarget as Node | null;
          if (related && current && current.contains(related)) return;
          onCountryLeave();
        }}
      />
      {activeData && (
        <GeoJson
          {...mapProps}
          data={activeData}
          hover={false}
          styleCallback={activeStyleCallback}
        />
      )}
      {hoverData && (
        <GeoJson
          {...mapProps}
          data={hoverData}
          hover={false}
          styleCallback={hoverStyleCallback}
        />
      )}
    </>
  );
};

export default CountryShapesOverlay;
